import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useGetTicket, useUpdateTicket } from "../../api/tickets";
import { Button, Container, Fieldset, Text } from "@mantine/core";
import { useGetPeopleTagsBySubtype } from "../../api/tag";
import { useForm } from "react-hook-form";
import {
  Checkbox,
  MultiSelect,
  Select,
  TextInput,
} from "react-hook-form-mantine";
import { DatePickerInput } from "@mantine/dates";
import { Controller } from "react-hook-form";
import CountrySelect from "../CountrySelect";
import { capitalize } from "../../utils/textUtils";
import { useGetCustomFields, useGetTicketColumns } from "../../api/tickets";
import { extractDirtyData } from "../../utils/dirtyData";
import {
  toCustomFieldInputValue,
  fromCustomFieldInputValue,
  optionData,
} from "../../utils/customFieldValues";
import { hasClaimFields } from "../../utils/ticketClaim";
import ClaimStatus from "./ClaimStatus";

function Edit() {
  const { ticketId } = useParams();

  const { data: ticket, isError, error } = useGetTicket({ ticketId });

  if (isError) {
    return <Text c="red">{error.message}</Text>;
  }

  return ticket && <EditTicketForm ticket={ticket} />;
}

const HOLDER_FIELDS = ["firstName", "lastName", "email", "companyName"];

function EditTicketForm({ ticket }) {
  const [errors, setErrors] = useState({});
  const { data: categoryTags } = useGetPeopleTagsBySubtype({
    eventId: ticket.eventId,
    subtype: "categoryOfUser",
  });
  const { data: customFieldsData } = useGetCustomFields({
    eventId: ticket.eventId,
    ticketId: ticket.id,
  });
  const { data: columns } = useGetTicketColumns({
    eventId: ticket.eventId,
  });

  const customColumns = columns
    ? columns?.filter((col) => col.isCustom && col.isVisible)
    : [];

  const { mutate, isPending } = useUpdateTicket({ ticketId: ticket.id });

  const holderValues = {
    firstName: ticket.firstName ?? "",
    lastName: ticket.lastName ?? "",
    email: ticket.email ?? "",
    companyName: ticket.companyName ?? "",
    categoryOfUser: (ticket.tags || [])
      .filter((tag) => tag.subtype === "categoryOfUser")
      .map((tag) => String(tag.id)),
  };

  const {
    control,
    handleSubmit,
    formState: { dirtyFields, isDirty },
    reset,
  } = useForm({ defaultValues: holderValues });

  useEffect(() => {
    // A refetch must not throw away what the organizer is in the middle of
    // typing, so the stored values only land in an untouched form.
    if (!columns || !customFieldsData || isDirty) return;

    reset({
      ...holderValues,
      ...customColumns.reduce((acc, field) => {
        const stored = customFieldsData.find(
          (cf) => cf.name === field.name && cf.ticketId === ticket.id
        );
        acc[field.name] = toCustomFieldInputValue(
          stored?.value,
          field.type,
          field.options
        );
        return acc;
      }, {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, customFieldsData, ticket, reset]);

  const handleTicketUpdate = (data) => {
    const dirtyData = extractDirtyData(data, dirtyFields);

    const payload = Object.entries(dirtyData).reduce((acc, [name, value]) => {
      if (HOLDER_FIELDS.includes(name) || name === "categoryOfUser") {
        acc[name] = value;
        return acc;
      }
      const field = customColumns.find((column) => column.name === name);
      acc.customFields = {
        ...acc.customFields,
        [name]: fromCustomFieldInputValue(value, field?.type),
      };
      return acc;
    }, {});

    // Saved without a category, the ticket gets Attendee (CS-1607).
    const attendeeTag = categoryTags?.find(
      (tag) => tag.systemKey === "attendee"
    );
    mutate(payload, {
      onSuccess: () =>
        reset({
          ...data,
          categoryOfUser:
            !data.categoryOfUser?.length && attendeeTag
              ? [String(attendeeTag.id)]
              : data.categoryOfUser,
        }),
    });

    setErrors({});
  };

  const renderField = (field) => {
    const options = optionData(field.options || []);

    if (field.type === "date") {
      return (
        <Controller
          key={field.name}
          name={field.name}
          control={control}
          render={({ field: dateField }) => (
            <DatePickerInput
              label={field.name}
              placeholder="Pick date"
              valueFormat="DD.MM.YYYY."
              clearable
              value={dateField.value ?? null}
              onChange={(date) => dateField.onChange(date ?? null)}
            />
          )}
        />
      );
    }

    if (field.type === "boolean") {
      return (
        <Checkbox
          key={field.name}
          control={control}
          name={field.name}
          label={field.name}
          mt="sm"
        />
      );
    }

    if (field.type === "country") {
      return (
        <CountrySelect
          key={field.name}
          control={control}
          name={field.name}
          label={field.name}
          placeholder="Select country"
        />
      );
    }

    if (field.type === "select") {
      return (
        <Select
          key={field.name}
          control={control}
          name={field.name}
          label={field.name}
          data={options}
          clearable
        />
      );
    }

    if (field.type === "multiselect") {
      return (
        <MultiSelect
          key={field.name}
          control={control}
          name={field.name}
          label={field.name}
          data={options}
          clearable
        />
      );
    }

    return (
      <TextInput
        key={field.name}
        label={field.name}
        control={control}
        name={field.name}
        error={Object.hasOwn(errors, field.name) && errors[field.name]}
        onChange={(e) => {
          // Reset the error message when the user starts typing
          setErrors((prev) => {
            const { [field.name]: thisFieldError, ...rest } = prev;
            return rest;
          });
          // If user types in only space, show an error message
          if (e.target.value.trim().length === 0 && e.target.value.length > 0) {
            setErrors((prev) => ({
              ...prev,
              [field.name]: "Field value cannot contain only spaces",
            }));
          }
        }}
      />
    );
  };

  // Shown once the API sends claim fields; until then the one-line note
  // below is all there is to say about the account.
  const showClaimStatus = hasClaimFields(ticket);

  return (
    <Container my="md">
      {/* Outside the form: its actions save on their own, not with the holder
          fields. Holder edits never change who holds the ticket. */}
      {showClaimStatus && <ClaimStatus ticket={ticket} />}

      <form onSubmit={handleSubmit(handleTicketUpdate)}>
        <Fieldset legend="Ticket holder">
          {!ticket.userId && !showClaimStatus && (
            <Text size="sm" c="dimmed">
              This ticket has no linked account.
            </Text>
          )}
          <TextInput
            label="First Name"
            name="firstName"
            control={control}
            mt="sm"
          />
          <TextInput
            label="Last Name"
            name="lastName"
            control={control}
            mt="sm"
          />
          <TextInput
            label="Email"
            type="email"
            name="email"
            control={control}
            mt="sm"
          />
          <TextInput
            label="Company"
            name="companyName"
            control={control}
            mt="sm"
          />
          <MultiSelect
            label="Category"
            placeholder="Select category"
            data={categoryTags?.map((tag) => ({
              value: String(tag.id),
              label: capitalize(tag.title),
            }))}
            control={control}
            name="categoryOfUser"
            mt="sm"
          />
        </Fieldset>

        {customColumns.length > 0 && (
          <Fieldset legend="Custom fields" mt="md">
            {customColumns.map((field) => renderField(field))}
          </Fieldset>
        )}

        <Button
          type="submit"
          variant="light"
          color="blue"
          mt="md"
          disabled={!isDirty || Object.keys(errors).length > 0}
          loading={isPending}
        >
          Update ticket
        </Button>
      </form>
    </Container>
  );
}

export default Edit;
